import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Edit, Trash2, Briefcase } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PowerTeamsPage = () => {
  const { chapter } = useAuth();
  const [powerTeams, setPowerTeams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "", status: "active" });

  const token = localStorage.getItem("token");
  const primaryColor = chapter?.primary_color || "#CC0000";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [ptRes, catRes] = await Promise.all([
        fetch(`${API}/power-teams`, { headers }),
        fetch(`${API}/categories`, { headers })
      ]);
      if (ptRes.ok) setPowerTeams(await ptRes.json());
      if (catRes.ok) setCategories(await catRes.json());
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editing ? `${API}/power-teams/${editing.power_team_id}` : `${API}/power-teams`;
      const response = await fetch(url, {
        method: editing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editing ? "Power Team updated" : "Power Team created");
        setDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        toast.error("Operation failed");
      }
    } catch (error) {
      toast.error("Error saving power team");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Deactivate this power team?")) return;
    try {
      const response = await fetch(`${API}/power-teams/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Power Team deactivated");
        fetchData();
      }
    } catch (error) {
      toast.error("Error deactivating");
    }
  };

  const resetForm = () => {
    setFormData({ name: "", description: "", status: "active" });
    setEditing(null);
  };

  const openEdit = (pt) => {
    setEditing(pt);
    setFormData({ name: pt.name, description: pt.description || "", status: pt.status });
    setDialogOpen(true);
  };

  const getCategoryCount = (ptId) => categories.filter(c => c.power_team_id === ptId).length;

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div></div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="power-teams-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-slate-900">Power Teams</h1>
            <p className="text-muted-foreground mt-1">Manage your chapter's power teams</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button style={{ backgroundColor: primaryColor }} data-testid="add-powerteam-btn">
                <Plus className="mr-2 h-4 w-4" />
                Add Power Team
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editing ? "Edit Power Team" : "Add Power Team"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" style={{ backgroundColor: primaryColor }}>{editing ? "Update" : "Create"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {powerTeams.map((pt, index) => (
            <Card 
              key={pt.power_team_id} 
              className="border-0 shadow-sm hover:shadow-md transition-shadow animate-fade-in"
              style={{ animationDelay: `${index * 0.05}s` }}
              data-testid={`powerteam-card-${pt.power_team_id}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg" style={{ backgroundColor: `${primaryColor}15` }}>
                      <Briefcase className="h-6 w-6" style={{ color: primaryColor }} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{pt.name}</h3>
                      <p className="text-sm text-muted-foreground">{getCategoryCount(pt.power_team_id)} categories</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(pt)}>
                        <Edit className="mr-2 h-4 w-4" />Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(pt.power_team_id)} className="text-red-600">
                        <Trash2 className="mr-2 h-4 w-4" />Deactivate
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {pt.description && <p className="mt-4 text-sm text-slate-600">{pt.description}</p>}
                <div className="mt-4">
                  <Badge className={pt.status === "active" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-700"}>
                    {pt.status}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {powerTeams.length === 0 && (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12 text-center text-muted-foreground">
              No power teams yet. Create your first power team to organize categories.
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default PowerTeamsPage;
